// =============================================================================
// T-322 — Aide complémentaire : vue d'ensemble des prestations (Hub Conformité)
// Une ligne par prestation TEF : où en est chaque client de l'offre. Lecture seule.
// Source : GET /api/aides/prestations/vue-ensemble (même calcul que l'écran d'un dossier).
// =============================================================================
import { useState, useEffect, useCallback, useMemo, useRef } from "react";
import { HandCoins, Bell, Hourglass, FileText, CheckCircle2, ExternalLink, Download, Users } from "lucide-react";
import { OHGrid } from "../../components/ui/oh-grid";
import { TintedBadge } from "../../components/ui/oh-tinted";
import { API, getAuthHeaders } from "../metier/dossier/helpers";
import { ETATS } from "../metier/dossier/AidesContractualisationPanel";
import { ETAPES, FACTURATION } from "../metier/dossier/AidesSuiviPanel";
import { formatDateFR } from "../../utils/dateFormat";

const eur = (v) =>
  `${Number(v || 0).toLocaleString("fr-FR", { minimumFractionDigits: 0, maximumFractionDigits: 2 })} €`;
const FAMILLE_COULEUR = { info: "blue", success: "green", error: "red" };
const COULEUR_FACTURATION = {
  RIEN_A_FACTURER: "gray", A_FACTURER: "orange", EN_ATTENTE_REGLEMENT: "amber",
  REGLE: "green", ECART_A_REGULARISER: "red", CONDITIONS_A_METTRE_A_JOUR: "gray",
};
const LIBELLE_ETAPE = Object.fromEntries(ETAPES.map((e) => [e.code, e.label]));
const nombre = (v) => Number(v || 0);

const MESSAGE_ERREUR = {
  401: "Session expirée : reconnectez-vous pour voir les prestations.",
  403: "Votre rôle n'a pas accès à la vue d'ensemble des prestations d'aides.",
};

export function libelleEtat(code) {
  return ETATS[code]?.label || code || "—";
}

// Export CSV — même forme que HubGrid : BOM, séparateur « ; », guillemets doublés.
export function construireCsv(rows) {
  const colonnes = [
    ["Client", (r) => r.client],
    ["Dossier", (r) => r.dossier_reference],
    ["État", (r) => libelleEtat(r.statut)],
    ["Étape", (r) => LIBELLE_ETAPE[r.etape_courante] || r.etape_courante],
    ["Aides recensées", (r) => r.aides_recensees],
    ["Démarches en cours", (r) => r.demarches_ouvertes],
    ["Relances dues", (r) => r.relances_dues],
    ["Montant obtenu (€)", (r) => nombre(r.montant_obtenu)],
    ["Montant accordé (€)", (r) => nombre(r.montant_accorde)],
    ["Forfait (€)", (r) => (r.forfait == null ? "" : nombre(r.forfait))],
    ["Facturé (€)", (r) => (r.facture == null ? "" : nombre(r.facture))],
    ["Réglé (€)", (r) => (r.regle == null ? "" : nombre(r.regle))],
    ["Reste à régler (€)", (r) => (r.reste_a_regler == null ? "" : nombre(r.reste_a_regler))],
    ["Facturation", (r) => FACTURATION[r.facturation] || r.facturation || ""],
    ["Créée le", (r) => formatDateFR(r.created_at)],
  ];
  const q = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const lignes = rows.map((r) => colonnes.map(([, f]) => q(f(r))).join(";"));
  return "﻿" + [colonnes.map(([t]) => q(t)).join(";"), ...lignes].join("\n");
}

function telecharger(rows) {
  const blob = new Blob([construireCsv(rows)], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `prestations_aides_${new Date().toISOString().split("T")[0]}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function PrestationsAidesPage({ tenantId, onOpenDossier }) {
  const [lignes, setLignes] = useState([]);
  const [tronque, setTronque] = useState(false);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState(null);
  const visibles = useRef([]);
  const surLignesVisibles = useCallback((rows) => { visibles.current = rows; }, []);

  const charger = useCallback(async () => {
    setChargement(true);
    setErreur(null);
    try {
      const res = await fetch(`${API}/api/aides/prestations/vue-ensemble`, {
        headers: getAuthHeaders(tenantId),
        credentials: "include",
      });
      if (!res.ok) {
        setLignes([]);
        setErreur(MESSAGE_ERREUR[res.status] || `Chargement impossible (erreur ${res.status}).`);
      } else {
        const data = await res.json();
        setLignes(Array.isArray(data.lignes) ? data.lignes : []);
        setTronque(!!data.tronque);
      }
    } catch {
      setLignes([]);
      setErreur("Chargement impossible : le serveur ne répond pas.");
    } finally {
      setChargement(false);
    }
  }, [tenantId]);

  useEffect(() => { charger(); }, [charger]);

  const columns = useMemo(() => [
    { key: "client", label: "Client",
      filter: { type: "text", placeholder: "Client…" },
      render: (v, r) => (
        <span style={{ color: "var(--oh-text)" }}>
          {v || "—"}
          {r.dossier_reference && (
            <span className="block text-xs" style={{ color: "var(--oh-text-muted)" }}>{r.dossier_reference}</span>
          )}
        </span>
      ) },
    { key: "statut", label: "État",
      filter: { type: "select", options: Object.entries(ETATS).map(([value, e]) => ({ value, label: e.label })) },
      render: (v) => (
        <TintedBadge color={FAMILLE_COULEUR[ETATS[v]?.famille] || "gray"}>{libelleEtat(v)}</TintedBadge>
      ) },
    { key: "etape_courante", label: "Étape",
      filter: { type: "select", options: ETAPES.map((e) => ({ value: e.code, label: e.label })) },
      render: (v) => LIBELLE_ETAPE[v] || v || "—" },
    { key: "aides_recensees", label: "Aides recensées", sortValue: (r) => nombre(r.aides_recensees),
      render: (v) => nombre(v) },
    { key: "demarches_ouvertes", label: "Démarches en cours", sortValue: (r) => nombre(r.demarches_ouvertes),
      render: (v) => nombre(v) },
    { key: "relances_dues", label: "Relances dues", sortValue: (r) => nombre(r.relances_dues),
      render: (v) => (nombre(v) > 0
        ? <TintedBadge color="red">{nombre(v)}</TintedBadge>
        : <span style={{ color: "var(--oh-text-muted)" }}>0</span>) },
    { key: "montant_obtenu", label: "Montant obtenu", sortValue: (r) => nombre(r.montant_obtenu),
      render: (v, r) => (
        <span style={{ color: "var(--oh-text)" }} title={`Accordé : ${eur(r.montant_accorde)}`}>{eur(v)}</span>
      ) },
    { key: "facturation", label: "Facturation",
      filter: { type: "select", options: Object.entries(FACTURATION).map(([value, label]) => ({ value, label })) },
      render: (v, r) => (v
        ? (
          <span>
            <TintedBadge color={COULEUR_FACTURATION[v] || "gray"}>{FACTURATION[v] || v}</TintedBadge>
            {r.facture != null && nombre(r.facture) > 0 && (
              <span className="block text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Facturé {eur(r.facture)} · réglé {eur(r.regle)}
              </span>
            )}
          </span>
        )
        : "—") },
    { key: "reste_a_regler", label: "Reste à régler", sortValue: (r) => nombre(r.reste_a_regler),
      render: (v) => (v == null ? "—" : eur(v)) },
    { key: "created_at", label: "Créée le", render: (v) => formatDateFR(v) || "—" },
  ], []);

  const stats = useMemo(() => [
    { key: "total", label: "Prestations", value: lignes.length, icon: Users, color: "blue", filter: () => true },
    { key: "relances", label: "Avec relance due", icon: Bell, color: "red",
      value: lignes.filter((l) => nombre(l.relances_dues) > 0).length,
      filter: (l) => nombre(l.relances_dues) > 0 },
    { key: "attente", label: "Demandes en attente", icon: Hourglass, color: "amber",
      value: lignes.filter((l) => nombre(l.demandes_en_attente) > 0).length,
      filter: (l) => nombre(l.demandes_en_attente) > 0 },
    { key: "a_facturer", label: "À facturer", icon: FileText, color: "orange",
      value: lignes.filter((l) => l.facturation === "A_FACTURER").length,
      filter: (l) => l.facturation === "A_FACTURER" },
    { key: "regle", label: "Réglées", icon: CheckCircle2, color: "green",
      value: lignes.filter((l) => l.facturation === "REGLE").length,
      filter: (l) => l.facturation === "REGLE" },
  ], [lignes]);

  const rowActions = useMemo(() => (onOpenDossier
    ? [{ label: "Ouvrir le dossier", icon: ExternalLink, onClick: (l) => onOpenDossier(l.dossier_id) }]
    : []), [onOpenDossier]);

  const bulkActions = useMemo(() => [
    { label: (n) => `Exporter la sélection (${n})`, icon: Download,
      onClick: (ids) => telecharger(lignes.filter((l) => ids.includes(l.id))) },
  ], [lignes]);

  const headerActions = useMemo(() => [
    { label: "Exporter (CSV)", icon: Download, onClick: () => telecharger(visibles.current) },
  ], []);

  return (
    <div className="space-y-4 p-4" data-testid="prestations-aides-page">
      <div className="flex items-center gap-3">
        <HandCoins className="w-5 h-5" style={{ color: "var(--oh-accent-text)" }} />
        <div>
          <h1 className="text-lg font-medium" style={{ color: "var(--oh-text)" }}>Prestations d'aides</h1>
          <p className="text-sm" style={{ color: "var(--oh-text-secondary)" }}>
            Où en est chaque client de l'offre d'aide complémentaire.
          </p>
        </div>
      </div>

      {erreur && (
        <div role="alert" data-testid="prestations-aides-erreur" className="rounded-lg px-4 py-3 text-sm"
          style={{ background: "var(--oh-error-bg)", color: "var(--oh-error-text)", border: "1px solid var(--oh-error-border)" }}>
          {erreur}
        </div>
      )}
      {tronque && (
        <div role="status" data-testid="prestations-aides-tronque" className="rounded-lg px-4 py-3 text-sm"
          style={{ background: "var(--oh-info-bg)", color: "var(--oh-info-text)", border: "1px solid var(--oh-info-border)" }}>
          La liste est limitée aux prestations les plus récentes : les plus anciennes ne sont pas affichées.
        </div>
      )}

      <OHGrid
        testIdPrefix="prestations-aides"
        data={lignes}
        columns={columns}
        stats={stats}
        loading={chargement}
        onRefresh={charger}
        defaultSortKey="created_at"
        defaultSortDir="desc"
        searchPlaceholder="Rechercher un client ou un dossier…"
        searchFilter={(l, q) =>
          `${l.client} ${l.dossier_reference}`.toLowerCase().includes(q)}
        emptyMessage={erreur ? "Aucune donnée à afficher" : "Aucune prestation d'aide pour le moment"}
        rowActions={rowActions}
        selectable
        bulkActions={bulkActions}
        headerActions={headerActions}
        onRowsChange={surLignesVisibles}
        pagination
        pageSize={50}
      />
    </div>
  );
}
